import { Button, Dialog, DialogActions, DialogTitle } from '@mui/material';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useState,
} from 'react';

type ConfirmContextType = (message: string) => Promise<boolean>;

type ConfirmStateType = {
    message: string;
    resolve: (value: boolean) => void;
};

const ConfirmContext = createContext<ConfirmContextType | null>(null);

export function ConfirmProvider({
    children,
}: Readonly<{ children: ReactNode }>) {
    // prettier-ignore
    const [confirmState, setConfirmState] = useState<ConfirmStateType | null>(null);

    const confirm = useCallback(
        (message: string) => {
            let isActive = true; // Track whether the component is still mounted

            return new Promise<boolean>((resolve) => {
                if (!isActive) return; // Prevent setting state if unmounted
                setConfirmState({ message, resolve });

                return () => {
                    isActive = false; // Cleanup when component unmounts
                };
            });
        },
        [setConfirmState],
    );

    const handleClose = (result: boolean) => {
        if (confirmState) {
            confirmState.resolve(result);
            setConfirmState(null);
        }
    };

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {confirmState && (
                <Dialog open onClose={() => handleClose(false)}>
                    <DialogTitle sx={{ whiteSpace: 'pre-line' }}>
                        {confirmState.message}
                    </DialogTitle>
                    <DialogActions>
                        <Button onClick={() => handleClose(false)}>
                            Cancel
                        </Button>
                        <Button onClick={() => handleClose(true)} color='error'>
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </ConfirmContext.Provider>
    );
}

export const useConfirm = () => {
    const context = useContext(ConfirmContext);
    if (!context)
        throw new Error('useConfirm must be used within a ConfirmProvider');
    return context;
};
